import { Buffer } from "buffer";
import { storeload } from "dstucrypt-algos";
import { Priv } from "jkurwa";

import {
  DER_TAG,
  MODERN_CIPHER_NAME,
  MODERN_KDF_NAME,
} from "./modern-store.constants";
import {
  ILoadModernStoreParams,
  IModernStore,
  IPbes2Params,
} from "./modern-store.models";
import {
  oidName,
  parseDer,
  readPbes2Params,
  stripPkcs7Padding,
  walkDerNodes,
} from "./modern-store.utils";

/**
 * Reads key containers issued on the post-2022 national standards (Купина +
 * Калина). They are ordinary PKCS#12, but the key bags are encrypted with
 * Калина-256-CBC under a key derived through KMAC-Купина. jkurwa 1.22.7 refuses
 * them outright — `spec/pbes.js` hard-checks the ГОСТ algorithm ids and has no
 * OID for either — so the parse is done here and the crypto comes from
 * `dstucrypt-algos`. See ai/docs/kep-signing.md.
 *
 * Remove this module once jkurwa gains native support.
 */

/** Every `pkcs8ShroudedKeyBag` in the container, with its PBES2 parameters. */
export const findEncryptedBags = (file: Buffer): IPbes2Params[] => {
  const bags: IPbes2Params[] = [];

  walkDerNodes(parseDer(file), node => {
    if (node.tag !== DER_TAG.sequence || node.children.length < 2) {
      return;
    }

    const [bagId, bagValue] = node.children;

    if (bagId.tag !== DER_TAG.oid || oidName(bagId) !== "pkcs8ShroudedKeyBag") {
      return;
    }

    // bagValue is the [0] context tag; its child is the EncryptedPrivateKeyInfo.
    const encryptedInfo = bagValue.children[0];

    if (!encryptedInfo) {
      return;
    }

    const params = readPbes2Params(encryptedInfo);

    if (params) {
      bags.push(params);
    }
  });

  return bags;
};

/** True when a bag uses KMAC-Купина or Калина — i.e. jkurwa cannot open this file. */
export const looksLikeModernStore = (file: Buffer): boolean =>
  findEncryptedBags(file).some(
    bag => bag.kdf === MODERN_KDF_NAME || bag.enc === MODERN_CIPHER_NAME,
  );

/**
 * Decrypts a modern container and returns its private keys, or `null` when the
 * file holds no modern bag (the caller then falls through to the normal
 * container diagnosis). Throws only when a modern bag was found but no key came
 * out of it — for a wrong password that surfaces as an ASN.1 error from
 * `Priv.from_asn1` on the garbage plaintext.
 */
export const loadModernStore = ({
  file,
  password,
}: ILoadModernStoreParams): IModernStore | null => {
  const keys: IModernStore["keys"] = [];
  let modernBags = 0;

  findEncryptedBags(file).forEach(params => {
    if (params.kdf !== MODERN_KDF_NAME && params.enc !== MODERN_CIPHER_NAME) {
      return; // legacy bag — jkurwa itself would have opened it
    }

    modernBags += 1;

    const decrypted = stripPkcs7Padding(
      Buffer.from(storeload(params, password)),
    );
    const store = Priv.from_asn1(decrypted, true);

    store.keys.forEach(priv => {
      if (!keys.some(existing => existing.d.equals(priv.d))) {
        keys.push(priv);
      }
    });
  });

  if (modernBags === 0) {
    return null;
  }

  if (keys.length === 0) {
    throw new Error(
      "modern container recognised but no private key was recovered",
    );
  }

  return { keys, format: "privkeys" };
};
